<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
				<div class="col-md-12">
          <h3 v-if="pricing.id">{{ pricing.name }}</h3>
          <h3 v-else>{{ _('New Pricing') }}</h3>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true, 'PricingDetails')" />
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <div v-bind="tabPanel('pricing')" class="tab-pane active" id="pricing" v-if="isTabActive('pricing')" key="pricing">
          <div class="row">
            <div id="productsorter" class="col-sm-6 leftcol">
              <div class="form-group languageversioned">
                <label for="name">{{ _('Name') }}</label>
                <input type="text" class="form-control" id="pricingName" v-model="pricing.name" :placeholder="_('Enter a name for the pricing model')">
              </div>
              <hr />
              <div class="panel panel-default">
                <div class="panel-heading">{{ _('Tickets') }}</div>
                <draggable v-model="pricing.sharedProduct" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-ticket sortable-list sorted-ticket list-group' }}">
                  <li v-for="ticket in pricing.sharedProduct" class="list-group-item flex-row" v-on:click="editTicketDialog(ticket)">
                    <span class="ticket-info" v-if="!!parseInt(ticket.default) || !!parseInt(ticket.selectable)">
                      <span v-if="!!parseInt(ticket.default)" class="fa fa-fw fa-star-o"></span>
                      <span v-if="!!parseInt(ticket.selectable)" class="fa fa-fw fa-hand-pointer-o"></span>
                    </span>
                    <span class="flex-row space-between" style="flex: 1"><span>{{ ticket.name }}</span><span>ID:{{ ticket.id }}<template v-if="parseInt(ticket.deleted)">({{ _('deleted') }})</template></span></span>
                  </li>
                </draggable>
              </div>
              <div class="form-group">
                <div class="input-group">
                  <select id="select-ticket" class="form-control" :placeholder="_('Select a ticket or enter new ticket name...')" v-model="ticketToAdd">
                    <option v-for="ticket in selectableTickets" :value="ticket.id">{{ ticket.name }} - ID:{{ ticket.id }}</option>
                  </select>
                  <span class="input-group-btn">
                    <button id="add-ticket" type="button" class="btn btn-success" v-on:click="addTicket">{{ _('Add') }}</button>
                  </span>
                </div>
              </div>
            </div>
            <div class="col-sm-6 rightcol">
              <label></label>
              <p>{{ _('Pricing models consist of one or more ticket types. This means that all those tickets will be available for each event the pricing model is used in.') }}</p>
              <p>{{ _('Some ticket types may be left unselectable, in which case they can only be selected from the POS interface.') }}</p>
            </div>
          </div>
          <div class="row" id="zoneRows" v-if="allZones && allZones.length && !isZonePricing">
            <div id="zonepricings" class="col-sm-6 leftcol">
              <h4>{{ _('Zone Pricings') }}</h4>
              <div class="panel panel-default">
                <div class="panel-heading">{{ _('Zone') }}<span class="pull-right">{{ _('Pricing') }}</span></div>
                <draggable v-model="pricing.ownPricing_zone" tag="ul" draggable=".list-group-item" :delay="100" :delayOnTouchOnly="true" :componentData="{ attrs: { class: 'o-sortable-zone sortable-list sorted-zone list-group' }}">
                  <li v-for="zoneLink in pricing.ownPricing_zone" class="list-group-item" v-on:click="editZoneDialog(zoneLink)">{{ zoneLink.zone.name }}
                    <span class="pricing-info pull-right">{{ zoneLink.subpricing.name }}</span>
                  </li>
                </draggable>
              </div>
              <div class="form-group" v-if="selectableZones && selectableZones.length">
                <div class="input-group">
                  <select id="select-zone" class="form-control" :placeholder="_('Select a zone...')" v-model="zoneToAdd">
                    <option v-for="zone in selectableZones" :value="zone.id">{{ zone.name }}</option>
                  </select>
                  <span class="input-group-btn">
                    <button id="add-zone" type="button" class="btn btn-success" v-on:click="addZoneDialog" :disabled="!zoneToAdd">{{ _('Add') }}</button>
                  </span>
                </div>
              </div>
            </div>
            <div class="col-sm-6 rightcol">
              <label></label>
              <p>{{ _('If your seat maps have multiple zones with different prices, you can select pricings for all the areas here.') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <nav class="modal-footer">
      <div class="flex-row space-between">
        <div class="button-col">
          <button class="btn btn-danger" v-on:click="remove"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
          <button class="btn btn-default" v-if="pricing.id" v-on:click="copyDialog" :disabled="unsavedChanges"><i class="fa fa-fw fa-copy"></i><span class="hidden-xs"> {{ _('Copy') }}</span></button>
        </div>
        <div class="button-col">
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import currencies from '~/lib/currencies'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import formats from '~/lib/formats'
import settingshelpers from '~/lib/settingshelpers'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      allTickets: [],
      allZones: [],
      allZonePricings: [],
      allVouchers: [],
      activeTab: 'pricing',
      pricing: {},
      ticketToAdd: null,
      zoneToAdd: null
    }
  },
  mixins: [changewatcher, components, currencies, formats, settingshelpers],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'pricing', label: this._('Pricing') }
      ])
    },
    ...mapGetters(['numberFormat']),
    versionStr () {
      return '&version=' + this.version
    },
    selectableTickets () {
      const tickets = this.pricing && this.pricing.sharedProduct ? this.pricing.sharedProduct.map(p => p.id) : []
      return this.allTickets.filter(t => !tickets.includes(t.id))
    },
    selectableZones () {
      const zones = this.pricing && this.pricing.ownPricing_zone ? this.pricing.ownPricing_zone.map(p => p.zone_id) : []
      return this.allZones.filter(z => !zones.includes(z.id))
    },
    isZonePricing () {
      return this.pricing && parseInt(this.pricing.type) === 1
    },
    vats () {
      return this.settings && this.settings.vats ? [...new Set([...this.settings.vats.split(';').sort((a, b) => b - a).map(v => parseFloat(v)), 0])] : [0]
    }
  },
  methods: {
    editTicketDialog (ticket) {
      this.showDialog({
        title: this._('Ticket'),
        width: 800,
        buttons: [
          {
            title: this._('Delete'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.removeTicket(ticket)
            }
          },
          {
            title: this._('Close'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('OK'),
            class: 'success',
            icon: 'check',
            requiredFields: ['name', 'price', 'vat'],
            handler: (data) => {
              this.saveTicket(ticket, data)
            }
          }
        ],
        fields: [
          {
            title: this._('Name'),
            type: 'text',
            name: 'name',
            placeholder: this._('Enter ticket name'),
            defaultValue: ticket.name,
            localized: true
          },
          {
            title: this._('Price'),
            type: 'text',
            name: 'price',
            placeholder: this._('Enter ticket price'),
            defaultValue: ticket.price,
            addon: this.currencySymbol(this.settings.currency)
          },
          {
            title: this._('Price in reporting'),
            type: 'text',
            name: 'reporting_price',
            placeholder: this._('Enter the price used in reporting') + ' (' + this._('leave empty to use actual price') + ')',
            defaultValue: ticket.reporting_price,
            addon: this.currencySymbol(this.settings.currency)
          },
          {
            title: this._('Value added tax'),
            type: 'select',
            name: 'vat',
            options: this.vats.map(v => { return { value: v, name: (parseInt(v, 10) != parseFloat(v) ? this.formatVat(v, this.numberFormat) : v) + ' %' }}),
            defaultValue: ticket.vat
          },
          {
            title: this._('Switch to this ticket when applying vouchers'),
            type: 'multiselect',
            name: 'voucher_ids',
            options: this.allVouchers.map(v => { return { value: v.id, name: v.id + ': ' + v.name }}),
            defaultValue: ticket.voucher_ids ? ticket.voucher_ids.split(',') : []
          },
          {
            title: this._('Selectable'),
            type: 'checkbox',
            name: 'selectable',
            defaultValue: ticket.selectable,
          },
          {
            title: this._('Default ticket'),
            type: 'checkbox',
            name: 'default',
            defaultValue: ticket.default
          },
          ...this.ticketLimitFields(ticket)
        ]
      })
    },
    // Activity pricing: quantity limits per ticket type. Empty values mean no limit.
    ticketLimitFields (ticket) {
      if (!this.isFeatureEnabled('activitypricing')) {
        return []
      }
      return [
        {
          title: this._('Minimum quantity per booking'),
          type: 'number',
          name: 'mincount',
          defaultValue: parseInt(ticket.mincount) > 0 ? ticket.mincount : '',
          help: this._('The minimum applies only when this ticket type is selected.')
        },
        {
          title: this._('Maximum quantity per booking'),
          type: 'number',
          name: 'maxcount',
          defaultValue: parseInt(ticket.maxcount) > 0 ? ticket.maxcount : ''
        }
      ]
    },
    saveTicket (ticket, data) {
      ['name', 'price', 'reporting_price', 'vat', 'selectable', 'default'].forEach(v => {
        if (data.hasOwnProperty(v)) {
          ticket[v] = data[v]
        }
      })
      if (this.isFeatureEnabled('activitypricing')) {
        ['mincount', 'maxcount'].forEach(v => {
          if (data.hasOwnProperty(v)) {
            const value = data[v] === null || data[v] === undefined ? '' : String(data[v]).trim()
            ticket[v] = value === '' ? null : value
          }
        })
      }
      if (typeof ticket.price === 'string') {
        ticket.price = ticket.price.replace(',', '.')
      }
      if (typeof ticket.reporting_price === 'string') {
        ticket.reporting_price = ticket.reporting_price.replace(',', '.')
      }
      ticket.voucher_ids = data.voucher_ids && data.voucher_ids.length ? data.voucher_ids.join(',') : null
      if (ticket.id.match && ticket.id.match(/^new-/)) {
        this.pricing.sharedProduct.push(ticket)
      }
    },
    removeTicket (ticket) {
      this.pricing.sharedProduct = this.pricing.sharedProduct.filter(p => p.id !== ticket.id)
    },
    addTicket () {
      if (this.ticketToAdd) {
        this.pricing.sharedProduct.push(this.allTickets.find(t => t.id === this.ticketToAdd))
        this.editTicketDialog(this.pricing.sharedProduct[this.pricing.sharedProduct.length - 1])
      } else {
        this.editTicketDialog({ id: 'new-' + DateTime.now().toUnixInteger() })
      }
      this.ticketToAdd = null
    },
    editZoneDialog (zoneLink) {
      const buttons = [
        {
          title: this._('Close'),
          icon: 'close',
          handler: () => {}
        },
        {
          title: this._('OK'),
          class: 'success',
          icon: 'check',
          requiredFields: ['subpricing'],
          handler: (data) => {
            if (zoneLink) {
              this.saveZone(zoneLink, data)
            } else {
              this.addZone(data)
            }
          }
        }
      ]
      if (zoneLink) {
        buttons.unshift({
          title: this._('Delete'),
          class: 'danger',
          icon: 'trash',
          handler: () => {
            this.removeZone(zoneLink)
          }
        })
      }
      this.showDialog({
        title: this._('Zone pricing'),
        width: 800,
        buttons: buttons,
        fields: [
          {
            title: this._('Pricing'),
            type: 'select',
            name: 'subpricing',
            options: this.allZonePricings.map(zp => { return { value: zp.id, name: zp.name }}),
            defaultValue: zoneLink ? zoneLink.subpricing_id : null
          }
        ]
      })
    },
    addZoneDialog () {
      this.editZoneDialog(null)
    },
    saveZone (zoneLink, data) {
      zoneLink.subpricing_id = data.subpricing
      zoneLink.subpricing = this.allZonePricings.find(zp => zp.id === data.subpricing)
    },
    addZone (data) {
      this.pricing.ownPricing_zone.push({
        id: 'new-' + DateTime.now().toUnixInteger(),
        pricing_id: this.pricing.id,
        zone_id: this.zoneToAdd,
        subpricing_id: data.subpricing,
        subpricing: this.allZonePricings.find(zp => zp.id === data.subpricing),
        zone: this.allZones.find(z => z.id === this.zoneToAdd)
      })
      this.zoneToAdd = null
    },
    removeZone (data) {
      this.pricing.ownPricing_zone = this.pricing.ownPricing_zone.filter(pz => pz.id !== data.id)
    },
    async loadAllTickets () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/products.json?iSortingCols=1&iSortCol_0=2&sSortDir=asc&sSearch_3=0-5-generic&apikey=' + this.apiKey + this.versionStr)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allTickets = response.data
    },
    async loadAllZones () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/zones.json?type=1&apikey=' + this.apiKey + this.versionStr)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allZones = response.data
    },
    async loadAllZonePricings () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/pricing.json?type=1&apikey=' + this.apiKey + this.versionStr)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allZonePricings = response.data
    },
    async loadAllVouchers () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/vouchers.json?external=true&iSortingCols=1&iSortCol_0=1&sSortDir=asc&apikey=' + this.apiKey  + this.versionStr)
      } catch(e) {
        this.showError(e)
        return
      }
      this.allVouchers = response.data
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'type',
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.pricing[e], a), {})
      data.tickets = JSON.stringify(this.pricing.sharedProduct)
      data.zones = JSON.stringify(this.pricing.ownPricing_zone)
      data.version = this.version
      return data
    },
    // Copies the pricing with new tickets of its own. Asks about the seat map zone pricings and the
    // discounts only when the pricing has any.
    async copyDialog () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/pricing/' + this.pricing.id + '/copy.json?apikey=' + this.apiKey)
      } catch(e) {
        return this.showError(e)
      }
      const { subpricings, discounts } = response.data
      const escape = s => String(s || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
      const fields = [
        {
          title: this._('Name'),
          type: 'text',
          name: 'name',
          placeholder: this._('Enter a name for the new pricing')
        },
        {
          type: 'info',
          text: this._('The copy gets new tickets with the same names, prices and settings. Changing them does not change the tickets of the original pricing.')
        }
      ]
      if (subpricings.length) {
        fields.push({
          type: 'info',
          text: this._('Zone pricings of the seat map') + ':<br>' + subpricings.map(s => escape(s.name) + (s.zones ? ' (' + escape(s.zones) + ')' : '')).join('<br>')
        }, {
          title: this._('Copy the zone pricings and their tickets too. Otherwise the copy uses the same zone pricings as the original.'),
          type: 'checkbox',
          name: 'copy_subpricings',
          defaultValue: '1'
        })
      }
      if (discounts.length) {
        fields.push({
          type: 'info',
          text: this._('Discounts that apply to the original tickets') + ':<br>' + discounts.map(d => escape(d.code)).join('<br>')
        }, {
          title: this._('Add the new tickets to these discounts'),
          type: 'checkbox',
          name: 'copy_discounts',
          defaultValue: '0'
        })
      }
      this.showDialog({
        title: this._('Copy pricing'),
        width: 800,
        buttons: [
          {
            title: this._('Close'),
            icon: 'close',
            handler: () => {}
          },
          {
            title: this._('Copy'),
            class: 'success',
            icon: 'copy',
            requiredFields: ['name'],
            handler: (data) => {
              this.copy(data, subpricings.length > 0, discounts.length > 0)
            }
          }
        ],
        fields
      })
    },
    async copy (data, hasSubpricings, hasDiscounts) {
      const copyData = { name: data.name }
      if (hasSubpricings) {
        copyData.copy_subpricings = data.copy_subpricings === '1' ? 1 : 0
      }
      if (hasDiscounts) {
        copyData.copy_discounts = data.copy_discounts === '1' ? 1 : 0
      }
      let response
      try {
        response = await axios.post(this.baseDomain + this.shopId + '/pricing/' + this.pricing.id + '/copy.json?apikey=' + this.apiKey, copyData)
      } catch(e) {
        return this.showError(this._('Copying the pricing failed!'))
      }
      this.showNotification(this._('Pricing copied.'))
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'PricingDetails',
        title: this._('Pricing') + ' ' + response.data + ': ' + data.name,
        width: 6,
        data: {
          pricingId: response.data
        }
      })
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/pricing/' + this.pricing.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deleting the pricing failed!'))
      }
      this.showNotification(this._('Pricing deleted!'))
      this.close()
    },
    async save () {
      const data = this.getSaveData()
      // check if we have default ticket (but only one)
      let defaultTicketCount = 0
      this.pricing.sharedProduct.forEach((ticket) => {
        if (parseInt(ticket.default, 10) === 1) {
          defaultTicketCount++
        }
      })
      if (defaultTicketCount === 0) {
        this.showError(this._('Pricing must contain at least one ticket set as default.'))
        return
      }
      if (defaultTicketCount > 1) {
        this.showError(this._('Pricing cannot contain more than one ticket set as default.'))
        return
      }
      this.saving = true
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/pricing/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/pricing.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadPricing(response.data.id)
      }
      this.saving = false
      this.saved = true
      this.storeState()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async reloadOnVersionChange () {
      if (!this.pricing || !this.pricing.id) {
        return
      }
      await this.loadPricing(this.pricing.id)
    },
    loadPricing (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/pricing/' + id + '.json?apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.showError(e)
          reject()
          this.loading = false
          return
        }
        this.pricing = response.data
        this.storeState()
        this.loading = false
        resolve(this.pricing)
      })
    }
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadPricing(this.component.initData.pricingId)
    }
    this.loadAllTickets()
    this.loadAllZones()
    this.loadAllZonePricings()
    this.loadAllVouchers()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.pricing && this.pricing.id && parseInt(this.pricing.id, 10) !== parseInt(this.component.initData.pricingId, 10)) {
        try {
          await this.loadPricing(this.component.initData.pricingId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['pricingUpdate'] = async (message) => {
        if (message && message.data && this.pricing && parseInt(message.data.id, 10) === parseInt(this.pricing.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadPricing(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>
